import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { ButtonComponent } from '../button/button.component';
import {
  PS_DELIVERY_COVERAGE,
  psDeliveryPriceFor,
} from '../../services/ps-delivery-coverage';
import { formatCrc } from '../../utils/currency.util';

interface DestinationGroup {
  readonly province: string;
  readonly price: number;
  readonly cantons: readonly string[];
}

/**
 * Modal INFORMATIVO (no bloqueante) con los destinos del «Mensajero Perrón Store»: por provincia
 * muestra su tarifa y los cantones cubiertos. Datos derivados de `PS_DELIVERY_COVERAGE` +
 * `psDeliveryPriceFor` (única fuente, espejo del backend). El copy (title/intro/cta) llega por inputs
 * para no importar `features/checkout` desde `shared/ui` (mismo criterio que `coverage-notice-dialog`).
 *
 * A diferencia del aviso de cambio de método, este SÍ se cierra con Escape y con el backdrop: es
 * consulta opcional, no una decisión que el usuario deba tomar. Focus-trap y scroll-lock (restaurando
 * el `overflow` previo, porque puede abrirse sobre el modal de precompra) siguen a `coverage-notice-dialog`.
 */
@Component({
  selector: 'app-ps-delivery-destinations-dialog',
  imports: [ButtonComponent],
  templateUrl: './ps-delivery-destinations-dialog.component.html',
  styleUrl: './ps-delivery-destinations-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PsDeliveryDestinationsDialogComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = input<boolean>(false);
  readonly title = input<string>('Destinos disponibles');
  readonly intro = input<string>('');
  readonly ctaLabel = input<string>('Cerrar');
  readonly mascotSrc = input<string>('/images/logo/info.png');
  /** Dimensiones intrínsecas del PNG (evitan CLS/deformación). Default = info.png. */
  readonly mascotWidth = input<number>(481);
  readonly mascotHeight = input<number>(640);
  readonly close = output<void>();

  private readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocusedElement: HTMLElement | null = null;
  private previousBodyOverflow = '';

  /** Provincias cubiertas con su tarifa y cantones (orden canónico de la whitelist). */
  protected readonly destinations: readonly DestinationGroup[] = Object.entries(
    PS_DELIVERY_COVERAGE,
  ).map(([province, cantons]) => ({
    province,
    price: psDeliveryPriceFor(province),
    cantons,
  }));

  constructor() {
    // Guardar/restaurar foco + bloqueo de scroll del body (restaurando el valor previo), según `open`.
    effect(() => {
      const isOpen = this.open();
      if (!isPlatformBrowser(this.platformId)) return;

      if (isOpen) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => {
          const root = this.dialogRef()?.nativeElement;
          if (root) this.getFocusableElements(root)[0]?.focus();
        });
      } else {
        document.body.style.overflow = this.previousBodyOverflow;
        if (this.previouslyFocusedElement) {
          const el = this.previouslyFocusedElement;
          this.previouslyFocusedElement = null;
          queueMicrotask(() => {
            if (document.contains(el)) el.focus({ preventScroll: true });
          });
        }
      }
    });

    // Escape cierra; Tab/Shift+Tab ciclan dentro del modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (!this.open()) return;

          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.onClose();
            return;
          }

          if (e.key !== 'Tab') return;

          const modal = this.dialogRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) {
            e.preventDefault();
            return;
          }

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected formatCRC(amount: number): string {
    return formatCrc(amount);
  }

  protected onClose(): void {
    this.close.emit();
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }
}
